import { createMultiPartRequest, createRequest } from '../helpers/api_utils';
import { sanitizeJobRequisition } from '../helpers/jobRequisitionUtils';
import {
  AddToInviteListRequest,
  AutoAssessment,
  AutoEvaluation,
  CandidateJobApplicantsData,
  IGetJobApplicants,
  JobApplicantCSV,
  JobApplicantEditRequest,
  JobApplicantSearchRequest,
  JobApplicantsStatusRequest,
  JobDescription,
  JobDescriptionTemplate,
  JobOpeningRequest,
  JobOpeningSkill,
  JobRequisition,
  NoteStructure,
  PatchProgress,
  SendInvitesByEmailRequest,
  SendInvitesRequest,
  jobOpeningTypes,
} from '../types/JobOpenings';
import { apiUrl } from './api_url';

const apiEndpoint = apiUrl + '/job-openings';

export async function recruiterUploadDocuments(
  jobOpeningId: string,
  file: File,
) {
  let formData = new FormData();
  formData.append('file', file);
  return fetch(
    createMultiPartRequest(
      apiEndpoint + `/${jobOpeningId}/document/upload`,
      'POST',
      true,
      formData,
    ),
  );
}
export async function createJobOpening(jobOpeningTypesList: string[]) {
  let body = {
    jobOpeningElements: jobOpeningTypesList,
  };
  return fetch(
    createRequest(apiEndpoint, 'POST', 'application/json', true, body),
  );
}

export async function editJobOpening(
  jobOpeningTypesList: jobOpeningTypes[],
  jobOpeningId: string,
) {
  let body = {
    jobOpeningElements: jobOpeningTypesList,
  };
  return fetch(
    createRequest(
      `${apiEndpoint}/${jobOpeningId}`,
      'PUT',
      'application/json',
      true,
      body,
    ),
  );
}

export async function getJobOpening(id: string) {
  return fetch(
    createRequest(apiEndpoint + `/${id}`, 'GET', 'application/json', true),
  );
}

export async function createJobRequisition(jobRequisition: JobRequisition) {
  const { id, ...body } = jobRequisition;

  // Sanitize the data to ensure hiringManager and internalRecruiter are string arrays
  const sanitizedBody = sanitizeJobRequisition({ id, ...body });
  const { id: _, ...finalBody } = sanitizedBody;

  return fetch(
    createRequest(
      apiEndpoint + '/job-requisitions',
      'POST',
      'application/json',
      true,
      finalBody,
    ),
  );
}

export async function createJobDescription(jobDescription: JobDescription) {
  // Extract jobTitle ID if it's an object, otherwise use the string value
  const jobTitleValue =
    typeof jobDescription.jobTitle !== 'string'
      ? jobDescription.jobTitle?.id
      : jobDescription.jobTitle;

  const body = {
    jobTitle: jobTitleValue,
    jobOpeningId: jobDescription.jobOpeningId,
    jobReferenceNumber: jobDescription.jobReferenceNumber,
    jobLocation: jobDescription.jobLocation,
    workType: jobDescription.workType,
    responsibilities: jobDescription.responsibilities,
    educations: jobDescription.educations,
    certifications: jobDescription.certifications,
    mandatorySkills: jobDescription.mandatorySkills,
    desirableSkills: jobDescription.desirableSkills,
    behavioralAttributes: jobDescription.behavioralAttributes,
    minimumWorkExperience: jobDescription.minimumWorkExperience,
    jobReferenceType: jobDescription.jobReferenceType,
    jobReferenceNumberAuto: jobDescription.jobReferenceNumberAuto,
    aiScoreWeights: jobDescription.aiScoreWeights,
    documentRequirements: jobDescription.documentRequirements,
  };
  return fetch(
    createRequest(
      apiEndpoint + '/job-descriptions',
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export async function getJobDescription(id: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/${id}/job-description`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function getUniqueIdJobDescription() {
  return fetch(
    createRequest(
      apiEndpoint + '/job-description/generate-id',
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function getJobOpeningDetails(id: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/${id}/details`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function getJobApplicants(
  id: string,
  search: string = '',
  page: number = 1,
  limit: number = 10,
  status: string = '',
) {
  const queryParams = new URLSearchParams({
    search: search,
    page: page.toString(),
    limit: limit.toString(),
    status: status,
  });

  const url = `${apiEndpoint}/${id}/applicants?${queryParams.toString()}`;

  return fetch(createRequest(url, 'GET', 'application/json', true));
}

export async function createAutoSuitability(autoSuitability: AutoEvaluation) {
  const body = { ...autoSuitability, id: undefined };
  return fetch(
    createRequest(
      apiEndpoint + '/auto-suitability',
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export async function createAutoOutstanding(autoEvaluation: AutoEvaluation) {
  const body = { ...autoEvaluation, id: undefined };
  return fetch(
    createRequest(
      apiEndpoint + '/auto-outstanding',
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export async function createAutoAssessment(autoAssessment: AutoAssessment) {
  const { id, ...body } = autoAssessment;
  return fetch(
    createRequest(
      apiEndpoint + '/auto-assessments',
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export async function getAutoAssessment(id: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/${id}/auto-assessments`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function createAutoBuildQuestions(
  autoAssessmentId: string,
  jobTitle: string,
) {
  const body = {
    autoAssessmentId: autoAssessmentId,
    jobTitle: jobTitle,
  };
  return fetch(
    createRequest(
      apiEndpoint + '/auto-build-questions',
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}
export async function setAutoAssessmentSkills(
  jobOpeningId: string,
  skills: JobOpeningSkill[],
) {
  const body = { jobOpeningId: jobOpeningId, skills: skills };
  return fetch(
    createRequest(
      apiEndpoint + '/auto-assessments/skills',
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export async function updateAutoAssessmentEmail(body: {
  jobOpeningId: string;
  emailTemplate: string;
  emailJsonTemplate: string;
}) {
  return fetch(
    createRequest(
      apiEndpoint + '/auto-assessments/email',
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export async function uploadFileJobDescription(
  jobOpeningId: string,
  file?: File,
  transcript?: string,
) {
  let formData = new FormData();
  if (file) {
    formData.append('file', file);
  } else if (transcript) {
    formData.append('transcript', transcript);
  }
  return fetch(
    createMultiPartRequest(
      apiEndpoint + `/${jobOpeningId}/job-description/file`,
      'POST',
      true,
      formData,
    ),
  );
}
export async function GetJobApplicants(
  jobOpeningId: string,
  searchObject: IGetJobApplicants,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/applicants`,
      'POST',
      'application/json',
      true,
      searchObject,
    ),
  );
}
export async function twoDJobApplicants(
  jobOpeningId: string,
  searchObject: JobApplicantSearchRequest,
  signal?: AbortSignal,
) {
  const request = createRequest(
    apiEndpoint + `/${jobOpeningId}/applicants/2d-graph`,
    'POST',
    'application/json',
    true,
    searchObject,
  );
  if (signal) {
    return fetch(request, { signal });
  }
  return fetch(request);
}

export async function saveFilter(
  jobOpeningId: string,
  searchObject: JobApplicantSearchRequest,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/applicants/filter`,
      'POST',
      'application/json',
      true,
      searchObject,
    ),
  );
}

export async function getFilter(jobOpeningId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/applicants/filter`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function searchJobApplicant(
  jobOpeningId: string,
  jobApplicantId: string,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/applicant/${jobApplicantId}`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function editJobApplicant(
  jobOpeningId: string,
  jobApplicantId: string,
  editObject: JobApplicantEditRequest,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/applicant/${jobApplicantId}`,
      'PUT',
      'application/json',
      true,
      editObject,
    ),
  );
}

export async function updateJobApplicantStatus(
  jobOpeningId: string,
  requestObject: JobApplicantsStatusRequest,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/applicant/applicant_status`,
      'POST',
      'application/json',
      true,
      requestObject,
    ),
  );
}

export async function getJobOpeningList(
  body: JobOpeningRequest,
  page: number = 1,
  limit: number = 10,
) {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  return fetch(
    createRequest(
      apiEndpoint + `/view?${queryParams.toString()}`,
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export async function getApplicantCsv(
  jobOpeningId: string,
  searchCSVObject: JobApplicantCSV,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/csv-job-applicant`,
      'POST',
      'application/json',
      true,
      searchCSVObject,
    ),
  );
}

export async function getCandidateApplicants(
  jobOpeningId: string,
  candidateJobApplicants: CandidateJobApplicantsData,
  page: number = 1,
  limit: number = 10,
) {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  return fetch(
    createRequest(
      apiEndpoint +
        `/${jobOpeningId}/candidate-applicants?${queryParams.toString()}`,
      'POST',
      'application/json',
      true,
      candidateJobApplicants,
    ),
  );
}

export async function sendInviteApplicants(
  jobOpeningId: string,
  selectedApplicants: string[],
) {
  const body = {
    applicantsId: selectedApplicants,
  };
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/invite-applicants`,
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export async function getInterviewRounds(jobOpeningId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/interview_round`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function statusPatch(jobOpeningId: string, body: PatchProgress) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/status`,
      'PATCH',
      'application/json',
      true,
      body,
    ),
  );
}

export async function saveCandidateToOffer(
  jobOpeningId: string,
  interviewApplicants: string[],
) {
  // Prepare the body as expected by the API
  // The API expects an array of applicant IDs
  const body = {
    applicants: interviewApplicants,
  };

  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/offer`,
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}
export async function getCandidateToOffer(
  jobOpeningId: string,
  status: string = 'Offer',
  page: number = 1,
  limit: number = 10,
) {
  const queryParams = new URLSearchParams({
    status: status,
    page: page.toString(),
    limit: limit.toString(),
  });

  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/offer?${queryParams.toString()}`,
      'GET',
      'application/json',
      true,
    ),
  );
}
export async function addCandidatesToOffer(jobOpeningId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobOpeningId}/offer`,
      'POST',
      'application/json',
      true,
    ),
  );
}

export async function getJDTemplateList(
  templateName: string = ' ',
  page: number = 1,
  limit: number = 10,
) {
  const queryParams = new URLSearchParams({
    search_term: templateName,
    page: page.toString(),
    limit: limit.toString(),
  });

  return fetch(
    createRequest(
      apiEndpoint + `/templates/list?${queryParams.toString()}`,
      'POST',
      'application/json',
      true,
    ),
  );
}

export function getJDTemplate(templateId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/templates/${templateId}`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function createJDTemplate(body: JobDescriptionTemplate) {
  return fetch(
    createRequest(
      apiEndpoint + '/job-description/templates',
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export function deleteJDTemplate(templateId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/templates/${templateId}`,
      'DELETE',
      'application/json',
      true,
    ),
  );
}

export async function createApplicantNote(
  jobApplicantId: string,
  note: NoteStructure,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/${jobApplicantId}/notes`,
      'POST',
      'application/json',
      true,
      note,
    ),
  );
}

export async function editApplicantNote(
  noteId: string,
  editNote: NoteStructure,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/edit/${noteId}/notes`,
      'PUT',
      'application/json',
      true,
      editNote,
    ),
  );
}

export async function deleteApplicantNote(noteId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/delete/${noteId}/notes`,
      'DELETE',
      'application/json',
      true,
    ),
  );
}

export async function sendUnsuccessfulEmail(
  jobOpeningId: string,
  emailData: {
    email: string;
    // applicantStatus: string[];
    // name: string;
  },
) {
  return fetch(
    createRequest(
      `${apiEndpoint}/${jobOpeningId}/job-applicants/unsuccessful-mail`,
      'POST',
      'application/json',
      true,
      emailData,
    ),
  );
}

// Invite List API functions
export async function addToInviteList(
  jobOpeningId: string,
  request: AddToInviteListRequest,
) {
  return fetch(
    createRequest(
      `${apiUrl}/invite-list/${jobOpeningId}`,
      'POST',
      'application/json',
      true,
      request,
    ),
  );
}

export async function getInviteList(
  jobOpeningId?: string,
  page: number = 1,
  pageSize: number = 10,
  search?: string,
  applied?: boolean,
) {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (jobOpeningId) {
    queryParams.append('job_opening_id', jobOpeningId);
  }

  if (search) {
    queryParams.append('search', search);
  }

  if (applied !== undefined) {
    queryParams.append('applied', applied.toString());
  }

  const url = `${apiUrl}/invite-list?${queryParams.toString()}`;

  return fetch(createRequest(url, 'GET', 'application/json', true));
}

export async function removeFromInviteList(
  jobOpeningId: string,
  inviteId: string,
) {
  return fetch(
    createRequest(
      `${apiUrl}/invite-list/${jobOpeningId}/${inviteId}`,
      'DELETE',
      'application/json',
      true,
    ),
  );
}

export async function sendInvites(
  jobOpeningId: string,
  request: SendInvitesRequest,
) {
  return fetch(
    createRequest(
      `${apiUrl}/invite-list/${jobOpeningId}/send`,
      'POST',
      'application/json',
      true,
      request,
    ),
  );
}

export async function sendInvitesByEmail(
  jobOpeningId: string,
  request: SendInvitesByEmailRequest,
) {
  return fetch(
    createRequest(
      `${apiUrl}/invite-list/${jobOpeningId}/send-by-email`,
      'POST',
      'application/json',
      true,
      request,
    ),
  );
}


export async function getPsychometricResults(
    id: string,
    page: number = 1,
    limit: number = 10
) {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  return fetch(
      createRequest(
          apiEndpoint + `/${id}/psychometric?${params}`,
          'GET',
          'application/json',
          true,
      ),
  );
}


export async function uploadPsychometricCSV(
    jobOpeningId: string,
    psychometricType: 'Staff' | 'Managers',
    file?: File,
) {
  let formData = new FormData();
  formData.append('psychometric_type', psychometricType);
  if (file) {
    formData.append('file', file);
  }
  return fetch(
      createMultiPartRequest(
          apiEndpoint + `/${jobOpeningId}/psychometric/import`,
          'POST',
          true,
          formData,
      ),
  );
}


export async function getPsychometricApplicants(
    jobOpeningId: string,
    page: number,
    limit: number,
    search?: string,
    emailSearch?: string,
) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (search?.trim()) params.append('search', search.trim());
  if (emailSearch?.trim()) params.append('email_search', emailSearch.trim());

  return fetch(
      createRequest(
          `${apiEndpoint}/${jobOpeningId}/psychometric/applicants?${params.toString()}`,
          'GET',
          'application/json',
          true,
      ),
  );
}

/* ------------------------------------------------------------------ *
 * Per-recruiter applicant action preferences
 *
 * Currently just whether moving a candidate to Unsuccessful should email
 * them immediately rather than leaving them Pending for a separate send.
 * Scoped to one recruiter on one opening: enabling it requires accepting a
 * warning, and nobody should be opted in by a colleague.
 * ------------------------------------------------------------------ */

export interface IApplicantActionPreferences {
  autoSendUnsuccessful: boolean;
  acknowledgedAt: string | null;
}

export async function getApplicantActionPreferences(jobOpeningId: string) {
  return fetch(
    createRequest(
      `${apiEndpoint}/${jobOpeningId}/applicant-action-preferences`,
      'GET', 'application/json', true,
    ),
  );
}

export async function updateApplicantActionPreferences(
  jobOpeningId: string,
  body: { autoSendUnsuccessful: boolean; acknowledged?: boolean },
) {
  return fetch(
    createRequest(
      `${apiEndpoint}/${jobOpeningId}/applicant-action-preferences`,
      'PUT', 'application/json', true, body,
    ),
  );
}
